import { BUSINESS_TAB_CONFIG } from '@/lib/navigation/businessNavConfig';
import type { BusinessTabRouteName } from '@/lib/navigation/businessNavConfig';
import { triggerSelectionHaptic } from '@/lib/platform/haptics';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { trackTabSwitched } from '@/lib/analytics/posthog';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { APP_TAB_BAR_CONTENT_HEIGHT } from '@/lib/useAppTabBarHeight';
import { showAccountContextSheet } from '@/features/workspaces/useAccountContextSheetStore';
import { formatBadgeCountLabel } from '@/lib/notifications/notifications';
import { getUnreadBusinessNotificationCount, useNotifications } from '@/features/pulse/useNotifications';
import { useAuth } from '@/lib/auth/auth';
import {
  createMaterialTopTabNavigator,
  type MaterialTopTabBarProps,
  type MaterialTopTabNavigationEventMap,
  type MaterialTopTabNavigationOptions,
} from '@react-navigation/material-top-tabs';
import type { ParamListBase, TabNavigationState } from '@react-navigation/native';
import { usePathname, withLayoutContext } from 'expo-router';
import * as React from 'react';
import { Pressable, Text, View, useWindowDimensions } from 'react-native';
import type { ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const MaterialTopTabs = createMaterialTopTabNavigator();
const PagerTabs = withLayoutContext<
  MaterialTopTabNavigationOptions,
  typeof MaterialTopTabs.Navigator,
  TabNavigationState<ParamListBase>,
  MaterialTopTabNavigationEventMap
>(MaterialTopTabs.Navigator, undefined, true);

function makeTabHandler(
  navigation: MaterialTopTabBarProps['navigation'],
  state: MaterialTopTabBarProps['state'],
  route: MaterialTopTabBarProps['state']['routes'][number]
) {
  return {
    onPress: () => {
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });
      if (!event.defaultPrevented && state.index !== state.routes.indexOf(route)) {
        navigation.navigate(route.name, route.params);
      }
    },
    onLongPress: () => navigation.emit({ type: 'tabLongPress', target: route.key }),
  };
}

function BusinessTabBar({ state, descriptors, navigation }: MaterialTopTabBarProps) {
  const insets = useSafeAreaInsets();
  const { currentContext, meaningfulContextCount } = useAccountContext();
  const organizationID = currentContext?.kind === 'business' ? currentContext.activeOrgId : null;
  const notificationsQuery = useNotifications({
    surface: 'business',
    organizationID,
  });
  const inboxBadgeLabel = formatBadgeCountLabel(
    getUnreadBusinessNotificationCount(notificationsQuery.data?.notifications ?? [])
  );
  const handleContextLongPress = React.useCallback(() => {
    void triggerSelectionHaptic();
    showAccountContextSheet();
  }, []);
  const bottomPadding = Math.max(insets.bottom, 8);
  const visibleRoutes = state.routes.filter((route) =>
    Object.prototype.hasOwnProperty.call(BUSINESS_TAB_CONFIG, route.name)
  );
  const barStyle: ViewStyle = {
    height: APP_TAB_BAR_CONTENT_HEIGHT + bottomPadding,
    paddingBottom: bottomPadding,
    paddingHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: DESIGN_TOKENS.colors.borderStrong,
    backgroundColor: DESIGN_TOKENS.colors.canvas,
  };

  return (
    <View pointerEvents="box-none" style={barStyle}>
      {visibleRoutes.map((route) => {
        const isFocused = state.index === state.routes.indexOf(route);
        const config = BUSINESS_TAB_CONFIG[route.name as BusinessTabRouteName];
        const { onPress, onLongPress: defaultOnLongPress } = makeTabHandler(
          navigation,
          state,
          route
        );
        const Icon = config.icon;
        const color = isFocused
          ? DESIGN_TOKENS.colors.accentStrong
          : DESIGN_TOKENS.colors.textMuted;
        const onLongPress =
          route.name === 'you' && meaningfulContextCount > 1
            ? handleContextLongPress
            : defaultOnLongPress;

        return (
          <Pressable
            key={route.key}
            accessibilityLabel={descriptors[route.key].options.tabBarAccessibilityLabel}
            accessibilityRole="tab"
            accessibilityState={isFocused ? { selected: true } : {}}
            testID={descriptors[route.key].options.tabBarButtonTestID}
            onPress={onPress}
            onLongPress={onLongPress}
            delayLongPress={220}
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <View
              style={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                paddingHorizontal: 12,
                paddingVertical: 4,
                paddingTop: 8,
                paddingBottom: 1,
                borderRadius: 12,
              }}>
              <View style={{ position: 'relative' }}>
                <Icon
                  color={color}
                  size={20}
                  strokeWidth={isFocused ? 2.3 : 1.8}
                  {...({} as any)}
                />
                {route.name === 'inbox' && inboxBadgeLabel ? (
                  <View
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -14,
                    }}>
                    <Text
                      style={{
                        minWidth: 20,
                        paddingHorizontal: 6,
                        paddingVertical: 2,
                        borderRadius: 999,
                        overflow: 'hidden',
                        backgroundColor: DESIGN_TOKENS.colors.accentPressed,
                        color: DESIGN_TOKENS.colors.canvas,
                        fontSize: 12,
                        lineHeight: 14,
                        textAlign: 'center',
                        fontFamily: DESIGN_TOKENS.typography.fonts.bodySemiBold,
                      }}>
                      {inboxBadgeLabel}
                    </Text>
                  </View>
                ) : null}
              </View>
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 14,
                  letterSpacing: 0.4,
                  fontFamily: DESIGN_TOKENS.typography.fonts.bodySemiBold,
                  color,
                }}>
                {config.label}
              </Text>
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

export default function BusinessTabsLayout() {
  const session = useAuth((state) => state.session);
  const { width: screenWidth } = useWindowDimensions();
  const lastTrackedRouteRef = React.useRef<string | null>(null);
  const pathname = usePathname();

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const titles: Record<BusinessTabRouteName, string> = {
      home: 'radious | Business Home',
      events: 'radious | Business Events',
      organization: 'radious | Business Venue',
      inbox: 'radious | Business Inbox',
      you: 'radious | Business Profile',
    };
    const hiddenTitle = pathname.includes('activity') ? 'radious | Activity Log' : null;
    const current =
      (Object.keys(titles) as BusinessTabRouteName[]).find((name) => pathname.includes(name)) ??
      'home';
    document.title = hiddenTitle ?? titles[current];
  }, [pathname]);

  const handlePagerStateChange = React.useCallback(
    (event: { data?: { state?: TabNavigationState<ParamListBase> } }) => {
      const nextState = event.data?.state;
      const nextRoute = nextState?.routes[nextState.index]?.name;
      if (
        !nextRoute ||
        !(nextRoute in BUSINESS_TAB_CONFIG) ||
        lastTrackedRouteRef.current === nextRoute
      ) {
        return;
      }
      if (lastTrackedRouteRef.current === null) {
        lastTrackedRouteRef.current = nextRoute;
        return;
      }
      lastTrackedRouteRef.current = nextRoute;
      void triggerSelectionHaptic();
      trackTabSwitched(`business:${nextRoute}`);
    },
    []
  );

  return (
    <View style={{ flex: 1 }}>
      <PagerTabs
        initialLayout={{ width: screenWidth }}
        screenListeners={{
          state: handlePagerStateChange,
        }}
        screenOptions={{
          animationEnabled: true,
          lazy: true,
          lazyPreloadDistance: 1,
          swipeEnabled: true,
          sceneStyle: {
            backgroundColor: DESIGN_TOKENS.colors.canvas,
          },
          tabBarStyle: { display: 'none' },
        }}
        tabBarPosition="bottom"
        tabBar={(props) => (session ? <BusinessTabBar {...props} /> : null)}>
        <PagerTabs.Screen name="home" options={{ title: 'radious | Business Home' }} />
        <PagerTabs.Screen name="events" options={{ title: 'radious | Business Events' }} />
        <PagerTabs.Screen name="organization" options={{ title: 'radious | Business Venue' }} />
        <PagerTabs.Screen name="inbox" options={{ title: 'radious | Business Inbox' }} />
        <PagerTabs.Screen name="you" options={{ title: 'radious | Business Profile' }} />
      </PagerTabs>
    </View>
  );
}
